import Store from 'Store/.';
import { Dispatch } from 'redux';
import { LoginView, MenuView } from 'Store/User/reducer';
import Api from 'Helpers/Api';

import { 
  UserAction, 
  setUser, 
  setIsLoginUser,
  setLoginView,
  setOrderHistory,
  setCountryList,
  setIsFetchingUser,
  setIsFetchingOrders,
  setMenuView,
  setNewsletterStatus,
} from 'Store/User/actions';
import moment from 'moment';

interface IHttpError {
  Message: string;
}

export const fetchLogin = (credentials: Julius.Website.CustomerLoginViewModel) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    const { isFetchingUser, loggedInUser } = Store.getState().user;

    if (isFetchingUser || loggedInUser) {
      return null;
    }

    dispatch(setIsFetchingUser(true));
    
    try {
      const response = await Api.post('Login', credentials);

      if (response.status === 200) {
        const user: Julius.Website.CustomerInfoViewModel = await response.json();
        dispatch(setUser(user, MenuView.INFO, LoginView.DEFAULT));
      } else {
        dispatch(setUser(null, MenuView.LOGIN, LoginView.LOGIN_FAILED));
      }
    } catch (e) {
      dispatch(setUser(null, MenuView.LOGIN, LoginView.LOGIN_FAILED));
    }
  }
};

export const fetchCurrentUser = () => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    const { loggedInUser, isFetchingUser } = Store.getState().user;

    if (loggedInUser || isFetchingUser) {
      return null;
    }

    dispatch(setIsFetchingUser(true));

    try {
      const response = await Api.get('CurrentUser');
      const user: Julius.Website.CustomerInfoViewModel = await response.json();
      
      if (response.status === 200 && user) {
        dispatch(setUser(user, MenuView.INFO, LoginView.LOGIN_SUCCESS));
      } else {
        dispatch(setUser(null, MenuView.LOGIN, LoginView.DEFAULT));
      }
    } catch (e) {
      dispatch(setUser(null, MenuView.LOGIN, LoginView.DEFAULT));
    } 
  }
};

export const fetchIsLoginUser = (email: string) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    dispatch(setIsFetchingUser(true));

    try {
      const response = await Api.get('IsLoginCustomer', `email=${ email }`)
      const isLoginUser: boolean = await response.json();
      dispatch(setIsLoginUser(isLoginUser, isLoginUser ? email : null));
    } catch (e) {
      dispatch(setIsLoginUser(false, null));
    }
  }
};

export const fetchNewPassword = (email: string) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    try {
      const response = await Api.get('NewPassword', `email=${ email }`);
      const loginView = response.status === 200 ? 
        LoginView.NEW_PASSWORD_SENT : LoginView.NEW_PASSWORD_FAILED;

      dispatch(setLoginView(loginView));
    } catch (e) {
      dispatch(setLoginView(LoginView.NEW_PASSWORD_FAILED));
    }
  }
};

export const fetchLogout = () => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    const { loggedInUser } = Store.getState().user;

    try {
      const response = await Api.get('Logout');

      if (response.status === 200) {
        dispatch(setUser(null, MenuView.LOGIN, LoginView.DEFAULT));
      } else {
        dispatch(setUser(loggedInUser, MenuView.ACTION_FAILED));
      }
    } catch (e) {
      dispatch(setUser(loggedInUser, MenuView.ACTION_FAILED));
    }
  }
};

export const createAccount = (userInfo: Julius.Website.CreateCustomerViewModel) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    try {
      const response = await Api.post('LoginCustomer', userInfo);

      if (response.status === 200) {
        const newUser: Julius.Website.CustomerInfoViewModel = await response.json();
        dispatch(setUser(newUser, MenuView.CREATE_ACCOUNT_SUCCESS));
      } else {
        const menuView = response.status === 403 ? 
          MenuView.CREATE_ACCOUNT_ALREADY_EXISTS : MenuView.ACTION_FAILED;

        const error: IHttpError = await response.json();
        dispatch(setMenuView(menuView, error.Message));
      }
    } catch (e) {
      dispatch(setMenuView(MenuView.ACTION_FAILED));
    }
  }
};

export const updateAccount = (userInfo: Julius.Website.CreateCustomerViewModel) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    try {

      const response = await Api.put('LoginCustomer', userInfo);

      if (response.status === 200) {
        const newUser: Julius.Website.CustomerInfoViewModel = await response.json();
        dispatch(setUser(newUser, MenuView.UPDATE_ACCOUNT_SUCCESS));
      } else {
        const error: IHttpError = await response.json();
        dispatch(setMenuView(MenuView.ACTION_FAILED, error.Message));
      }
    } catch (e) {
      dispatch(setMenuView(MenuView.ACTION_FAILED));
    }
  }
};

export const updatePassword = (passwordInfo: Julius.Website.ChangePasswordViewModel) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    try {
      const response = await Api.put('LoginPassword', passwordInfo);

      if (response.status === 200) {
        const isSuccess: boolean = await response.json();

        const menuView = isSuccess ? 
          MenuView.UPDATE_PASSWORD_SUCCESS : MenuView.UPDATE_PASSWORD_FAILED;

        dispatch(setMenuView(menuView));
      } else {
        const error: IHttpError = await response.json();
        dispatch(setMenuView(MenuView.UPDATE_PASSWORD_FAILED, error.Message));
      }
    } catch (e) {
      dispatch(setMenuView(MenuView.UPDATE_PASSWORD_FAILED));
    }
  }
};

export const createPasswordToCustomer = (password: string) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    try {
      const response = await Api.post('PasswordToCustomer', password);

      if (response.status === 200) {
        const isSuccess: boolean = await response.json();
        dispatch(setIsLoginUser(isSuccess));
      } else {
        dispatch(setIsLoginUser(false));
      }
    } catch (e) {
      dispatch(setIsLoginUser(false));
    }
  }
};

export const fetchOrderHistory = () => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    const { isFetchingOrders } = Store.getState().user;

    if (isFetchingOrders) {
      return null;
    }

    dispatch(setIsFetchingOrders(true));

    try {
      const response = await Api.get('OrdersForCustomer');
      const orders: Julius.Website.OrderSmallViewModel[] = await response.json();
      dispatch(setOrderHistory(orders.map((o: Julius.Website.OrderSmallViewModel) => ({
        ...o,
        performanceDate: moment(o.performanceDate).toDate(),
        expireDate: moment(o.expireDate).toDate(),
      }))));
    } catch (e) {
      dispatch(setOrderHistory(null));
    } finally {
      dispatch(setIsFetchingOrders(false));
    }
  } 
};

export const fetchCountryList = () => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    const { countryList } = Store.getState().user;

    if (countryList && countryList.length > 0) {
      return null;
    }

    try {
      const response = await Api.get('CountryList');
      const countries: Julius.Website.CountryItemViewModel[] = await response.json();
      
      if (response.status === 200) {
        dispatch(setCountryList(countries));
      } else {
        dispatch(setCountryList([]));
      }
    } catch (e) {
      dispatch(setCountryList([]));
    }
  }
};

export const fetchNewsletterSubscription = (email: string) => {
  return async (dispatch: Dispatch<UserAction>): Promise<void> => {
    const { newsletterSuccess, newsletterEmail } = Store.getState().user;

    if (email === newsletterEmail && newsletterSuccess) {
      return null;
    }

    try {
      const response = await Api.get('NewsletterSubscription', `email=${ email }`);
      const isSuccess: boolean = await response.json();
      
      if (response.status === 200 && isSuccess) {
        dispatch(setNewsletterStatus(true, email));
      } else {
        dispatch(setNewsletterStatus(false, email));
      }
    } catch (e) {
      dispatch(setNewsletterStatus(false, email));
    }
  }
};


// WEBPACK FOOTER //
// ./src/store/User/thunks.tsx